<template>
    <div class="thinking" :class="{ 'is-embedded': embedded, 'is-illustrated': art !== 'none' }">
        <template v-if="art !== 'none'">
            <CalendarWeekLoader v-if="art === 'week'" />
            <InsightDiscsLoader v-else-if="art === 'discs'" />
            <ProfileMarksLoader v-else-if="art === 'marks'" />
            <PerspectiveWheelLoader v-else :land="land" @landed="emit('landed')" />

            <div class="copy">
                <component :is="embedded ? 'h2' : 'h1'" class="illustrated-title">{{ label }}</component>
                <div v-if="stages.length" class="illustrated-stage">
                    <Transition name="stage" mode="out-in"
                        ><span :key="activeStage">{{ stages[activeStage] }}</span></Transition
                    >
                </div>
                <BaseButton v-if="showContinue" theme="tertiary" class="illustrated-continue" @click="emit('continue')">{{
                    t("onboarding.calendarFirst.thinking.continueAnyway")
                }}</BaseButton>
            </div>
        </template>

        <template v-else>
            <StepHeader :embedded="embedded" :title="label" :lede="status" />

            <!-- The pipelines expose no progress values, so the bar is indeterminate. Hide it from
                 assistive technology because the host's live region already announces status.
                 -->
            <div class="bar" aria-hidden="true"><span class="bar-sweep"></span></div>

            <div v-if="stages.length" class="stage">
                <span aria-hidden="true" class="stage-disc"><i class="bi bi-stars"></i></span>
                <Transition name="stage" mode="out-in"
                    ><span :key="activeStage">{{ stages[activeStage] }}</span></Transition
                >
            </div>

            <!-- Keep skeleton layouts consistent with their results to avoid a second layout change when
                 content arrives.
                 -->
            <div v-if="skeleton === 'readings'" class="ghost" aria-hidden="true">
                <ReadingSkeletonCard v-for="(slot, index) in READING_SLOTS" :key="slot" :index="index" />
            </div>

            <!-- The escape hatch is the point of this screen: generation runs on
             infrastructure built for weekly batches, so a user must never be
             stuck here. -->
            <BaseButton v-if="showContinue" theme="tertiary" class="continue" @click="emit('continue')">{{ t("onboarding.calendarFirst.thinking.continueAnyway") }}</BaseButton>
        </template>
    </div>
</template>

<script setup lang="ts">
/**
 * Reuse result components for skeletons to avoid a layout change that makes completion look like another
 * load.
 */
import ReadingSkeletonCard from "~vue/CalendarFirstOnboarding/ReadingSkeletonCard.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import PerspectiveWheelLoader from "~vue/components/PerspectiveWheelLoader.vue";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import CalendarWeekLoader from "./CalendarWeekLoader.vue";
import InsightDiscsLoader from "./InsightDiscsLoader.vue";
import ProfileMarksLoader from "./ProfileMarksLoader.vue";
import StepHeader from "./StepHeader.vue";

const {
    label,
    embedded = false,
    showContinue = false,
    status = "",
    stages = [],
    skeleton = "none",
    art = "none",
    land = null,
} = defineProps<{
    label: string;
    embedded?: boolean;
    showContinue?: boolean;
    status?: string;
    stages?: string[];
    skeleton?: "readings" | "none";
    /** When art is enabled, it replaces the progress bar and result skeletons. */
    art?: "week" | "wheel" | "discs" | "marks" | "none";
    /**
     * Four-letter type for the wheel to land on, or null while the result is still being fetched. The
     * wheel keeps turning until it has one, then emits `landed` when its arrival is finished playing.
     */
    land?: string | null;
}>();

const emit = defineEmits<{ continue: []; landed: [] }>();
const { t } = useI18n();

/** The pipeline produces three readings, so reserve three skeleton slots. */
const READING_SLOTS = 3;

/*
 * Pipelines report no stage progress. Advance stages by elapsed time, keeping the last active until the run
 * ends.
 */
const STAGE_DWELL_MS = 9000;
const elapsed = ref(0);
let ticker: ReturnType<typeof setInterval> | null = null;

const activeStage = computed(() => Math.min(Math.floor(elapsed.value / STAGE_DWELL_MS), Math.max(stages.length - 1, 0)));

onMounted(() => {
    ticker = setInterval(() => {
        elapsed.value += 1000;
    }, 1000);
});

onBeforeUnmount(() => {
    if (ticker !== null) {
        clearInterval(ticker);
    }
});
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    /* Match the other steps' 672px column so loading placeholders have the same width as the results. */
    .thinking {
        @apply mx-auto w-full max-w-168 px-6 py-10;
    }

    /* The guided setup panel controls width and padding; embedded screens must not add another inset. */
    .thinking.is-embedded {
        @apply max-w-none px-0 py-0;
    }

    .thinking.is-illustrated {
        @apply flex flex-col items-center gap-10 pt-10;
    }

    .copy {
        @apply flex w-full flex-col items-center gap-3 text-center;
    }

    .illustrated-title {
        @apply display-small-serif text-ink;
    }

    /* Keep stage text height fixed to prevent content below it shifting when messages wrap differently. */
    .illustrated-stage {
        @apply scaffold-body flex h-14 items-center justify-center px-5 text-ink-muted;
    }

    .illustrated-continue {
        @apply body-small font-normal text-ink-muted;
    }

    /*
     * Match the results column width to avoid layout shifts. Keep the track visible between animation
     * sweeps.
     */
    .bar {
        @apply relative mt-6 h-1 w-full overflow-hidden rounded-full bg-chip-nadia-text/18;
    }

    /*
     * The color ledger permits magenta on bars. Feather the sweep's ends to avoid implying a completion
     * percentage the pipeline cannot report.
     */
    .bar-sweep {
        @apply absolute inset-y-0 w-[45%] rounded-full;
        background-image: linear-gradient(90deg, transparent, var(--color-valence-pink-400) 35%, var(--color-chip-nadia-text) 65%, transparent);
        animation: bar-sweep 1.8s ease-in-out infinite;
    }

    /* Match the assessment intro's step discs so users recognize the same step during loading. */
    .stage {
        @apply body-small mt-6 flex items-center gap-4 text-ink;
    }

    .stage-disc {
        @apply flex size-10 shrink-0 items-center justify-center rounded-full bg-chip-nadia-bg text-[17px] text-chip-nadia-text;
    }

    .stage-enter-active,
    .stage-leave-active {
        @apply transition-opacity duration-300;
    }

    .stage-enter-from,
    .stage-leave-to {
        @apply opacity-0;
    }

    /*
     * The 20px margin plus the first skeleton's 28px padding creates a 48px gap, matching the spacing above
     * the status band.
     */
    .ghost {
        @apply mt-5;
    }

    /* Omit the first divider so the status band does not look like another skeleton list item. */
    .ghost > :first-child {
        @apply border-t-0;
    }

    .continue {
        @apply mt-6 -ml-(--spacing-button-x);
    }
}

@keyframes bar-sweep {
    0% {
        transform: translateX(-100%);
    }
    100% {
        transform: translateX(250%);
    }
}

@media (prefers-reduced-motion: reduce) {
    .bar-sweep {
        animation: none;
        @apply w-full opacity-40;
    }
}
</style>
